import React from "react";
import { useSelector } from "react-redux";
import { AppState } from "store";
import useRevenueReportingService from "./useRevenueReportingService";

const resolvedTemplateChecks = new Map<number, boolean>();
const pendingTemplateChecks = new Map<number, Promise<boolean>>();

function useHasRevenueReportingTemplates(enabled: boolean = true) {
  const companyId = useSelector((store: AppState) => store.auth.selectedCompany?.id);
  const revenueReportingService = useRevenueReportingService();

  const isEnabled = enabled && companyId !== undefined;
  const cachedResult = companyId !== undefined ? resolvedTemplateChecks.get(companyId) : undefined;

  const [hasTemplates, setHasTemplates] = React.useState(cachedResult ?? false);
  const [loading, setLoading] = React.useState(isEnabled && cachedResult === undefined);

  const checkTemplates = (id: number) => {
    const pendingCheck = pendingTemplateChecks.get(id);
    if (pendingCheck) {
      return pendingCheck;
    }

    const check = revenueReportingService
      .getTemplates({ showErrors: false })
      .then((templates) => {
        const result = (templates?.length ?? 0) > 0;
        resolvedTemplateChecks.set(id, result);
        return result;
      })
      .catch(() => false)
      .finally(() => pendingTemplateChecks.delete(id));

    pendingTemplateChecks.set(id, check);
    return check;
  };

  React.useEffect(() => {
    if (!enabled || companyId === undefined) {
      setHasTemplates(false);
      setLoading(false);
      return;
    }

    const resolvedResult = resolvedTemplateChecks.get(companyId);
    if (resolvedResult !== undefined) {
      setHasTemplates(resolvedResult);
      setLoading(false);
      return;
    }

    let mounted = true;
    setLoading(true);
    checkTemplates(companyId).then((result) => {
      if (mounted) {
        setHasTemplates(result);
        setLoading(false);
      }
    });

    return () => {
      mounted = false;
    };
  }, [companyId, enabled]);

  return { hasTemplates, loading };
}

export default useHasRevenueReportingTemplates;
